import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  StagingConfig,
  CreateStagingConfigParams,
  ExcelDataRow,
  StagingRecordListItem,
} from '../_models/staging-config.interface';

@Injectable({
  providedIn: 'root',
})
export class StagingConfigService {
  configs = signal<StagingConfig[]>([]);
  isLoading = signal<boolean>(false);

  constructor(private http: HttpClient) {}

  async loadConfigs(projectId: number): Promise<void> {
    this.isLoading.set(true);
    try {
      const result = await firstValueFrom(
        this.http.get<{ staging_configs: StagingConfig[] }>(
          `${environment.apiURL}projects/${projectId}/staging_configs.json`
        )
      );
      this.configs.set(result.staging_configs);
    } catch (err) {
      console.error('Fehler beim Laden der Staging-Configs:', err);
    } finally {
      this.isLoading.set(false);
    }
  }

  async createConfig(projectId: number, params: CreateStagingConfigParams): Promise<StagingConfig | null> {
    try {
      const result = await firstValueFrom(
        this.http.post<{ staging_config: StagingConfig }>(
          `${environment.apiURL}projects/${projectId}/staging_configs.json`,
          params
        )
      );
      this.configs.update(list => [...list, result.staging_config]);
      return result.staging_config;
    } catch (err) {
      console.error('Fehler beim Erstellen:', err);
      return null;
    }
  }

  async updateConfig(configId: number, params: Partial<CreateStagingConfigParams> & { enabled?: boolean }): Promise<StagingConfig | null> {
    try {
      const result = await firstValueFrom(
        this.http.patch<{ staging_config: StagingConfig }>(
          `${environment.apiURL}staging_configs/${configId}.json`,
          params
        )
      );
      this.configs.update(list => list.map(c => c.id === configId ? result.staging_config : c));
      return result.staging_config;
    } catch (err) {
      console.error('Fehler beim Aktualisieren:', err);
      return null;
    }
  }

  async deleteConfig(configId: number): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.delete<{ success: boolean }>(
          `${environment.apiURL}staging_configs/${configId}.json`
        )
      );
      this.configs.update(list => list.filter(c => c.id !== configId));
      return true;
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
      return false;
    }
  }

  async uploadExcelData(configId: number, excelData: ExcelDataRow[]): Promise<boolean> {
    try {
      const result = await firstValueFrom(
        this.http.post<{ staging_config: StagingConfig; rows_imported: number }>(
          `${environment.apiURL}staging_configs/${configId}/upload_excel.json`,
          { excel_data: excelData }
        )
      );
      this.configs.update(list => list.map(c => c.id === configId ? result.staging_config : c));
      return true;
    } catch (err) {
      console.error('Fehler beim Excel-Upload:', err);
      return false;
    }
  }

  async loadExcelData(configId: number): Promise<{ rows: ExcelDataRow[]; authorize: boolean } | null> {
    try {
      const result = await firstValueFrom(
        this.http.get<{ excel_data: ExcelDataRow[]; authorize_serial_numbers: boolean }>(
          `${environment.apiURL}staging_configs/${configId}/excel_data.json`
        )
      );
      return { rows: result.excel_data, authorize: result.authorize_serial_numbers };
    } catch (err) {
      console.error('Fehler beim Laden der Excel-Daten:', err);
      return null;
    }
  }

  async updateExcelRow(configId: number, rowIndex: number, rowData: ExcelDataRow): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.patch<{ success: boolean }>(
          `${environment.apiURL}staging_configs/${configId}/update_excel_row.json`,
          { row_index: rowIndex, row_data: rowData }
        )
      );
      return true;
    } catch (err) {
      console.error('Fehler beim Aktualisieren der Excel-Zeile:', err);
      return false;
    }
  }

  async authorizeBulk(configId: number, serialNumbers: string[]): Promise<number> {
    try {
      const result = await firstValueFrom(
        this.http.post<{ success: boolean; authorized_count: number }>(
          `${environment.apiURL}staging_configs/${configId}/authorize_bulk.json`,
          { serial_numbers: serialNumbers }
        )
      );
      return result.authorized_count;
    } catch (err) {
      console.error('Fehler bei der Bulk-Freigabe:', err);
      return 0;
    }
  }

  async loadRecords(configId: number): Promise<StagingRecordListItem[]> {
    try {
      const result = await firstValueFrom(
        this.http.get<{ records: StagingRecordListItem[] }>(
          `${environment.apiURL}staging_configs/${configId}/records.json`
        )
      );
      return result.records;
    } catch (err) {
      console.error('Fehler beim Laden der Records:', err);
      return [];
    }
  }

  getReportUrl(configId: number, recordId: number): string {
    return `${environment.apiURL}staging_configs/${configId}/records/${recordId}/report`;
  }
}
